import {
  faBell,
  faComment,
  faComments,
  faExclamationTriangle,
  faFlag,
  faKey,
  faList,
  faMobileAlt,
  faRobot,
  faSquarePen,
  faTaxi,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CaseEventType, EventModel, TranslationLookups } from 'api/model';
import { FC, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { GenericFileViewer } from 'components/Files/FileViewer';
import Comment from './eventComponents/Comment';
import CommentEditor from './eventComponents/CommentEditor';
import { EventSection } from './eventComponents/components';
import DefaultEventHeader from './eventComponents/DefaultEventHeader';
import DeviationReportEvent from './eventComponents/DeviationReportEvent';
import TitledEvent from './eventComponents/TitledEvent';
import CaseAssignmentRequest from './eventComponents/CaseAssignmentRequest';
import GenericFileViewerModal from './EventAttachmentFileViewer';

const ThumbnailStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;
const ThumbnailButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
`;

interface AttachmentLike {
  fileGuid: string;
  fileEnding: string;
  caption?: string | null;
}

const ThumbnailCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 96px;
`;
const ThumbnailCaption = styled.div`
  margin-top: 4px;
  font-size: 11px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.foreground.secondary ?? '#555'};
  word-break: break-word;
`;

const DeliveryConditionAttachments: FC<{ attachments: AttachmentLike[] }> = ({
  attachments,
}) => {
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const preview = previewIdx != null ? attachments[previewIdx] : null;
  return (
    <>
      <ThumbnailStrip>
        {attachments.map((att, idx) => (
          <ThumbnailCell key={att.fileGuid}>
            <ThumbnailButton
              type="button"
              onClick={() => setPreviewIdx(idx)}
              title={att.caption || 'Klicka för att förstora'}
            >
              <GenericFileViewer
                fileGuid={att.fileGuid}
                fileEnding={att.fileEnding}
                maxHeight={96}
              />
            </ThumbnailButton>
            {att.caption && att.caption.trim() !== '' ? (
              <ThumbnailCaption>{att.caption}</ThumbnailCaption>
            ) : null}
          </ThumbnailCell>
        ))}
      </ThumbnailStrip>
      {preview && (
        <GenericFileViewerModal
          fileGuid={preview.fileGuid}
          fileEnding={preview.fileEnding}
          onClose={() => setPreviewIdx(null)}
          title={preview.caption || 'Leveransskick-bilaga'}
          description={preview.caption ?? undefined}
        />
      )}
    </>
  );
};

const HeaderTextSpan = styled.span`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 5px;
`;

const DivWithBreaks = styled.div`
  white-space: pre-wrap;
`;

interface Props {
  event: EventModel;
  translations: TranslationLookups;
  editMode?: boolean;
  onEditClick?(): void;
  onDeleteClick?(): void;
  onCancelSave?(): void;
  onSave?(
    caseId: number,
    message: string,
    eventId?: number,
    internal?: boolean,
    readByAdmin?: boolean
  ): void;
  onUpdate?(): void;
}

const Event: FC<Props> = ({
  event,
  translations,
  editMode,
  onEditClick,
  onDeleteClick,
  onCancelSave,
  onSave,
  onUpdate,
}) => {
  const theme = useTheme();
  const { caseEventTypes } = translations;

  switch (event.caseEventType) {
    case CaseEventType.TaxiRequest:
    case CaseEventType.BringVehicleHomeRequest:
    case CaseEventType.RequestAssignFieldTesterOutsideDrivingPolicy:
      return <CaseAssignmentRequest event={event} onUpdate={onUpdate} />;
    case CaseEventType.Comment:
    case CaseEventType.InternalComment:
      return editMode ? (
        <CommentEditor event={event} onCancel={onCancelSave} onSave={onSave} />
      ) : (
        <Comment
          event={event}
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
        />
      );

    case CaseEventType.CustomerStatusSMSSuccess:
      return (
        <TitledEvent
          event={event}
          icon={<FontAwesomeIcon icon={faMobileAlt} />}
          title="SMS-uppdatering utskickat"
        />
      );

    case CaseEventType.CustomerStatusSMSFail:
      return (
        <TitledEvent
          event={event}
          icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
          title="SMS-uppdatering FEL"
        />
      );

    case CaseEventType.RideComment:
      return (
        <TitledEvent
          event={event}
          icon={<FontAwesomeIcon icon={faComments} />}
          title="Ride-kommentar"
        />
      );

    case CaseEventType.ShareboxKeyDroppedIn:
      return (
        <TitledEvent
          event={event}
          icon={<FontAwesomeIcon icon={faKey} />}
          title="Nyckel i Sharebox"
        />
      );

    case CaseEventType.ShareboxKeyPickedUp:
      return (
        <TitledEvent
          event={event}
          icon={<FontAwesomeIcon icon={faKey} />}
          title="Nyckel uthämtad"
        />
      );

    case CaseEventType.RideChange:
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={
              <>
                <FontAwesomeIcon icon={faSquarePen} />{' '}
                {event.message || caseEventTypes[event.caseEventType]}
              </>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
        </EventSection>
      );
    case CaseEventType.DeliveryConditionRecorded: {
      // SubmitInspection lägger header på första raden ("Leveransskick
      // registrerat: <tier> (<rating>/10)") och FT-kommentaren på resten.
      // Vi splittar för att kunna rendera header som rubrik och kommentaren
      // som en separat body — annars hade DefaultEventHeader visat hela
      // klumpen utan radbrytningar.
      const message = event.message || caseEventTypes[event.caseEventType] || '';
      const newlineIdx = message.indexOf('\n');
      const headerLine =
        newlineIdx === -1 ? message : message.slice(0, newlineIdx);
      const bodyText =
        newlineIdx === -1 ? '' : message.slice(newlineIdx + 1).trim();
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={<span>{headerLine}</span>}
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          {bodyText && <DivWithBreaks>{bodyText}</DivWithBreaks>}
          {event.attachments && event.attachments.length > 0 && (
            <DeliveryConditionAttachments attachments={event.attachments} />
          )}
        </EventSection>
      );
    }
    case CaseEventType.CarKeyAndDrivingLicensePhoto:
    case CaseEventType.CarKeyPhoto:
    case CaseEventType.CarParkPhoto:
    case CaseEventType.WorkGearPhoto: {
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            attachmentFileEnding={event.attachmentFileEnding}
            attachmentFileGuid={event.attachmentFileGuid}
            created={event.created}
            headerText={
              <span>
                {event.message || caseEventTypes[event.caseEventType]}
              </span>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
        </EventSection>
      );
    }
    case CaseEventType.CarTransportTruckAssignmentLoadedCar:
    case CaseEventType.CarTransportTruckAssignmentUnLoadedCar:
    case CaseEventType.CarTransportTruckAssignmentUnLoadedAllCars:
    case CaseEventType.CarTransportTruckAssignmentLoadedAllCars: {
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            attachmentFileEnding={event.attachmentFileEnding}
            attachmentFileGuid={event.attachmentFileGuid}
            created={event.created}
            headerText={<span>{caseEventTypes[event.caseEventType]}</span>}
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          <DivWithBreaks>{event.message}</DivWithBreaks>
        </EventSection>
      );
    }
    case CaseEventType.FlaggedForSpeeding:
      return (
        <EventSection>
          <DefaultEventHeader
            created={event.created}
            headerText={
              <HeaderTextSpan>
                <FontAwesomeIcon
                  color={theme.colors.foreground.error}
                  icon={faTaxi}
                />
                <span>
                  {event.message || caseEventTypes[event.caseEventType]}
                </span>
              </HeaderTextSpan>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
        </EventSection>
      );
    case CaseEventType.DeviationReport:
      return <DeviationReportEvent event={event} translations={translations} />;
    case CaseEventType.AutoApprove:
      return (
        <EventSection>
          <DefaultEventHeader
            created={event.created}
            headerText={
              <HeaderTextSpan>
                <FontAwesomeIcon icon={faRobot} />
                <span>{caseEventTypes[event.caseEventType]}</span>
              </HeaderTextSpan>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          {event.message}
        </EventSection>
      );
    case CaseEventType.WorkGearReport:
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={
              <HeaderTextSpan>
                <FontAwesomeIcon icon={faList} />{' '}
                {caseEventTypes[event.caseEventType]}
                {!event.readByAdmin && (
                  <span>
                    <FontAwesomeIcon
                      color={theme.colors.foreground.newMessage}
                      icon={faBell}
                    />
                  </span>
                )}
              </HeaderTextSpan>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          <DivWithBreaks>{event.message}</DivWithBreaks>
        </EventSection>
      );

    case CaseEventType.AssignmentVehicleFaultDiscovered:
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={
              <HeaderTextSpan>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{caseEventTypes[event.caseEventType]}</span>
              </HeaderTextSpan>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          <DivWithBreaks>{event.message}</DivWithBreaks>
        </EventSection>
      );

    case CaseEventType.ExternalAdminChange: {
      // show icon if not read by admin
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={
              <HeaderTextSpan>
                <FontAwesomeIcon icon={faComment} />
                <span>{caseEventTypes[event.caseEventType]}</span>
                {!event.readByAdmin && (
                  <span>
                    <FontAwesomeIcon
                      color={theme.colors.foreground.newMessage}
                      icon={faBell}
                    />
                  </span>
                )}
              </HeaderTextSpan>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          <DivWithBreaks>{event.message}</DivWithBreaks>
        </EventSection>
      );
    }

    case CaseEventType.SystemMessage:
      return (
        <EventSection>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={
              <HeaderTextSpan>
                <FontAwesomeIcon icon={faFlag} />
                <span>{caseEventTypes[event.caseEventType]}</span>
              </HeaderTextSpan>
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          <DivWithBreaks>{event.message}</DivWithBreaks>
        </EventSection>
      );
    default:
      return (
        <EventSection key={event.id}>
          <DefaultEventHeader
            position={event.position}
            created={event.created}
            headerText={
              caseEventTypes[event.caseEventType] ?? event.message ?? '-'
            }
            httpTitle={
              event.assignmentID ? `Uppdrag ${event.assignmentID}` : ''
            }
            modified={event.modified}
            modifiedByID={event.modifiedByID}
            createdByID={event.createdByID}
            userName={event.createdBy}
          />
          <DivWithBreaks>{event.message}</DivWithBreaks>
        </EventSection>
      );
  }
};

export default Event;
